import { Input } from 'antd'
import React, { forwardRef } from 'react'

/**
 * 自定义输入框组件
 *
 * 这是一个基于Ant Design Input封装的输入框组件，提供了统一的样式和功能。
 * 使用forwardRef支持ref转发，便于父组件直接操作输入框。
 * 支持密码类型，当 type="password" 或 password={true} 时，使用 Input.Password 组件。
 *
 * @param className - 自定义CSS类名
 * @param password - 是否为密码输入框（优先级高于 type="password"）
 * @param props - 其他传递给Input组件的属性
 * @param ref - 转发给Input组件的ref引用
 *
 * 使用场景：
 * 1. 表单输入 - 各种表单中的文本输入
 * 2. 搜索框 - 页面中的搜索功能
 * 3. 数据录入 - 用户信息录入
 * 4. 配置设置 - 各种配置项的输入
 * 5. 过滤条件 - 数据过滤的输入条件
 * 6. 用户注册 - 注册表单中的输入框
 * 7. 编辑模式 - 可编辑表格中的输入
 * 8. 动态表单 - 动态生成的表单输入
 * 9. 密码输入 - 敏感信息的输入
 *
 * 使用示例：
 * ```tsx
 * // 基础用法
 * <CustomInput
 *   placeholder="请输入内容"
 *   onChange={(e) => setValue(e.target.value)}
 * />
 *
 * // 密码输入框（方式1：使用 password 属性）
 * <CustomInput
 *   password
 *   placeholder="请输入密码"
 *   onChange={(e) => setPassword(e.target.value)}
 * />
 *
 * // 密码输入框（方式2：使用 type="password"）
 * <CustomInput
 *   type="password"
 *   placeholder="请输入密码"
 *   onChange={(e) => setPassword(e.target.value)}
 * />
 *
 * // 带验证
 * <CustomInput
 *   placeholder="请输入邮箱"
 *   type="email"
 *   required
 *   onChange={handleEmailChange}
 * />
 *
 * // 使用ref
 * const inputRef = useRef<HTMLInputElement>(null)
 *
 * <CustomInput
 *   ref={inputRef}
 *   placeholder="可聚焦的输入框"
 * />
 *
 * // 自定义样式
 * <CustomInput
 *   className="custom-input-style"
 *   placeholder="自定义样式输入框"
 * />
 * ```
 */

// 定义输入框的属性类型
interface CustomInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** 是否为密码输入框，设置为 true 时会使用 Input.Password 组件 */
  password?: boolean
  /** Ant Design Input 的校验状态，传 'error' 时显示红色边框 */
  status?: 'error' | 'warning'
}

const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
  ({ className, password, type, ...props }, ref: any) => {
    // 如果 password 属性为 true，或者 type 为 "password"，则使用 Input.Password
    const isPassword = password || type === 'password'

    if (isPassword) {
      // Input.Password 不需要 type 属性，已经通过解构排除了
      return <Input.Password className={className} ref={ref} {...(props as any)} />
    }

    return <Input className={className} ref={ref} type={type} {...props} />
  }
)

export default CustomInput
